import { useMutation, useQueries, useQuery, useQueryClient, UseQueryResult } from "react-query"
import { notification } from "antd"
import * as statementsApi from "./statementsApi"
import { StatementDetail } from "./statement"

export const useStatementQuery = (statementId: string, options?: { disabled?: boolean }) => {
  return useQuery(['invoicing', 'statement', statementId], {
    queryFn: () => statementsApi.getStatement(statementId),
    enabled: !!statementId && !options?.disabled,
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading the statement.' })
    }
  })
}

export const useStatementMembersQueries = (statementIds: string[]): UseQueryResult<StatementDetail>[] =>
  useQueries(statementIds.map(statementId => ({
    queryKey: ['invoicing', 'statement', statementId],
    queryFn: () => statementsApi.getStatement(statementId),
    enabled: !!statementId,
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading the statement invoices.' })
    }
  }))) as UseQueryResult<StatementDetail>[]

export const useStatementPaymentStatusQuery = (statementId: string, options?: { disabled?: boolean }) => {
  return useQuery(['invoicing', 'statement-payment-status', statementId], {
    queryFn: () => statementsApi.getStatementPaymentStatus(statementId),
    enabled: !!statementId && !options?.disabled
  })
}

export const useMarkStatementPaidManuallyMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: statementsApi.markStatementPaidManually,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    }
  })
}

export const useMarkStatementNotPaidManuallyMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: statementsApi.markStatementNotPaidManually,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['invoicing'])
      const message = error.response?.data?.message ?? 'An error occurred while marking the statement not paid.'
      notification.error({ message })
    }
  })
}

export const useSendStatementMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: statementsApi.sendStatement,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while sending the statement.'
      notification.error({ message })
    }
  })
}

type DeleteStatementMutationData = {
  statementId: string
  deleteFromQuickBooks: boolean
}

export const useDeleteStatementMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ statementId, deleteFromQuickBooks }: DeleteStatementMutationData) =>
      statementsApi.deleteStatement(statementId, deleteFromQuickBooks),
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['invoicing'])
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while deleting the statement.'
      notification.error({ message })
    }
  })
}
